import Image from "next/image";
import Link from "next/link";
import Layout from "./layout/layout";
import TrendingNow from "./core/trending-now";
import SideBar from "./sidebar/sidebar";
import NewsActionButtons from "./core/news-action-buttons";
import useDateLocale from "hooks/useDateLocale";
import QrCodeContainer from "./core/qr-code-container";
import useOneLinkUrl from "hooks/useOneLinkUrl";
import StickyBox from "react-sticky-box";
import SwitchToAppDialog from "./core/switch-to-app-dialog";
import NewNewsCard from "./core/new-news-card";
import NewsStackOnNews from "@public/news-stack-on-news.png";
import useOnScreen from "hooks/useOnScreen";
import { formatDistanceToNowStrict } from "date-fns";
import { NewsPageQuery } from "@graphql/generated/graphql";
import { useMemo, useRef } from "react";
import { Trans } from "@lingui/macro";
import { useRouter } from "next/router";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { generateSubdomainPath } from "./layout/shared/link-props";
import { DialogName, triggerDialog } from "@components/core/dialog";
import { topCategories } from "./sidebar/side-bar-topics";
import QRCode from "react-qr-code";
import OverlayBImage from "@public/overlay-b-image.png";
import ArticleRating from "@public/rating/article-rating.png";
import dynamic from "next/dynamic";
import SideBarTopNews from "./sidebar/side-bar-top-news";

const CommentsBox = dynamic(() => import("@components/comments/comments-box"));

interface INewsSummaryPageProps {
  data: NewsPageQuery;
  loading: Boolean;
  fetchMore: any;
  limit: number;
}

const NewsSummaryPage: React.FunctionComponent<INewsSummaryPageProps> = ({
  data,
  loading,
  limit,
  fetchMore,
}) => {
  const switchToApp = useOneLinkUrl(
    "https://link.luminews.my/ewC9/switchtoappbutton"
  );
  const ref = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const refValue = useOnScreen(ref, router)[1];

  return (
    <Layout
      showNewsStacks={true}
      showCTA={false}
      showFooter={false}
      className="!overflow-visible"
      retainWholePage={false}
      refValue={refValue}
    >
      <div className="mx-auto my-6 flex max-w-7xl flex-row items-start px-4 2xs:px-6 lg:space-x-20">
        {loading || !data ? (
          <p className="flex-1 py-4 text-center font-sen text-text-lightest">
            <Trans>Loading...</Trans>
          </p>
        ) : data.news ? (
          <div>
            <NewsSummary news={data.news} />
            <div className="mt-8 lg:hidden">
              {data?.news?.commentsCount > 0 && (
                <CommentsBox
                  deeplink={switchToApp}
                  comments={data?.news?.comments}
                  commentsCount={data?.news?.commentsCount}
                  contentId={data?.news?.id}
                  contentType={"News"}
                  limit={limit}
                  fetchMore={fetchMore}
                />
              )}
            </div>
            <div>
              <div
                className="mt-12 font-poppins text-xl font-bold text-text-darkest"
                id="might-like-section"
              >
                {data.news.nextNews?.length > 0 && (
                  <>
                    {data.news.isUnderNewsStack ? (
                      <Trans>More articles in this stack</Trans>
                    ) : (
                      <Trans>You might like ⬇️</Trans>
                    )}
                    <div className="mt-4 h-[1px] w-full bg-off-white"></div>
                  </>
                )}
                {data.news.nextNews && (
                  <NewsListSection news={data.news.nextNews} />
                )}
              </div>
            </div>
            <div>
              <div className="mt-12 font-poppins text-xl font-bold text-text-darkest">
                {data.news.newsPublisher?.news.length > 0 && (
                  <>
                    <Trans>📖 Also from this publisher</Trans>
                    <div className="mt-4 h-[1px] w-full bg-off-white"></div>
                  </>
                )}
                {data.news.newsPublisher?.news && (
                  <NewsListSection news={data.news.newsPublisher.news} />
                )}
              </div>
            </div>
            <div className="relative my-16 md:my-8 md:flex">
              <div className="relative h-80 w-full md:h-[348px] md:w-[344px]">
                <Image
                  className="rounded-lg"
                  src={NewsStackOnNews}
                  alt="news-stack-on-news-image"
                  layout="fill"
                  objectFit="cover"
                />
              </div>

              <div className="flex flex-1 flex-col md:pl-6">
                <p className="mt-8 font-poppins text-3xl font-bold leading-[1.3] md:mb-0">
                  <Trans>Why read with Lumi?</Trans>
                </p>
                <p className="font-sen font-light text-text-medium">
                  <Trans>
                    Being in the know can be hard when we're drowning in a sea
                    of noise and information. Curate what you read and catch up
                    in much less time with the Lumi app.
                  </Trans>
                </p>
                <div className="flex space-x-4">
                  <Link href={switchToApp}>
                    <a className="mt-8 inline-flex">
                      <div className="flex items-center rounded-2xl bg-primary px-4 py-2">
                        <p className="text-center font-sen text-sm text-white">
                          <Trans>Download Now</Trans>
                        </p>
                      </div>
                    </a>
                  </Link>
                  <Link href={generateSubdomainPath("/why-use-lumi")}>
                    <a className="mt-8 inline-flex">
                      <div className="flex items-center rounded-2xl bg-secondary px-4 py-2">
                        <p className="text-center font-sen text-sm text-primary2">
                          <Trans>Learn more</Trans>
                        </p>
                        <ChevronRightIcon
                          className="h-6 w-6 text-primary2"
                          aria-hidden="true"
                        />
                      </div>
                    </a>
                  </Link>
                </div>
              </div>
            </div>
            <div className="block lg:hidden">
              <SideBarTopNews />
            </div>
            <div className="-mx-4 mt-8" ref={ref}>
              <TrendingNow />
            </div>
          </div>
        ) : (
          <p className="flex-1 py-4 text-center font-sen text-text-lightest">
            <Trans>No news found</Trans>
          </p>
        )}

        <StickyBox offsetTop={20} offsetBottom={20} className="space-y-10">
          <div className="hidden lg:block">
            {data?.news?.commentsCount > 0 && (
              <CommentsBox
                deeplink={switchToApp}
                comments={data?.news?.comments}
                commentsCount={data?.news?.commentsCount}
                contentId={data?.news?.id}
                contentType={"News"}
                limit={limit}
                fetchMore={fetchMore}
                loading={loading}
              />
            )}
            <div className="mt-8 hidden lg:block">
              <SideBarTopNews />
            </div>
          </div>
          <SideBar />
        </StickyBox>
      </div>
    </Layout>
  );
};

interface NewsSummaryProps {
  news: NewsPageQuery["news"];
}

const NewsSummary: React.FunctionComponent<NewsSummaryProps> = ({ news }) => {
  const { content, title, imageUrl, pubDate, newsPublisher, link, topics } =
    news;

  const utm_link = useMemo(() => {
    const url = new URL(link);

    url.searchParams.set("utm_source", "lumi");
    url.searchParams.set("utm_medium", "lumi_web");
    url.searchParams.set("utm_campaign", "article");

    return url.toString();
  }, [link]);

  const locale = useDateLocale();

  const openMoreTopicsDialog = (event) => {
    event.preventDefault();
    triggerDialog(DialogName.moreTopics);
  };

  return (
    <>
      <h1 className="font-poppins text-3xl font-bold text-text-darkest">
        {title}
      </h1>
      <div className="my-4 flex flex-row flex-wrap gap-2">
        {topics &&
          topics.map((topic) => (
            <Link
              href={`/news?topic=${topic.key || ""}`}
              key={topic.id}
              passHref
            >
              <a className="flex-shrink-0">
                <div
                  className={`rounded bg-primary2 px-2 py-1 font-sen text-xs text-white ${
                    topCategories.some((category) => category.key == topic.key)
                      ? ""
                      : "cursor-pointer"
                  }`}
                  onClick={(event) =>
                    !topCategories.some(
                      (category) => category.key == topic.key
                    ) && openMoreTopicsDialog(event)
                  }
                >
                  {topic.name} {topic.emoji}
                </div>
              </a>
            </Link>
          ))}
      </div>

      <div className="relative">
        <div className="relative h-52 w-full md:h-[434px]">
          <Image
            className="rounded-lg"
            src={imageUrl}
            alt={title}
            layout="fill"
            objectFit="cover"
            priority
          />
        </div>

        <div className="2xs:flex 2xs:items-center 2xs:justify-between">
          <div
            className="my-6 flex items-center justify-between"
            id="publisher-name"
          >
            <div className="relative h-[30px] w-[53px] drop-shadow-custom">
              <Link href={`/publishers/${newsPublisher.slug}`}>
                <a>
                  <Image
                    className="rounded-lg"
                    src={newsPublisher.imageUrl}
                    alt={newsPublisher.name}
                    layout="fill"
                    objectFit="cover"
                    priority
                  />
                </a>
              </Link>
            </div>

            <div className="ml-4 flex-1">
              <Link href={`/publishers/${newsPublisher.slug}`}>
                <a>
                  <span className="font-poppins text-sm font-bold">
                    {newsPublisher.name}
                  </span>
                </a>
              </Link>
            </div>
          </div>

          <p className="mb-4 font-sen text-xs text-text-medium 2xs:mb-0">
            {formatDistanceToNowStrict(new Date(pubDate), {
              locale,
              addSuffix: true,
            })}
          </p>
        </div>

        <div
          className="news-content break-words font-poppins text-text-dark"
          dangerouslySetInnerHTML={{ __html: content }}
        ></div>

        <div className="absolute inset-0 top-[80%] bottom-0 bg-gradient-to-b from-white/0 to-white"></div>
      </div>
      <div className="relative z-10 bg-white">
        <ReadMoreSection
          utmLink={utm_link}
          publisherName={newsPublisher.name}
        />
      </div>
      <div className="mt-8 flex items-center justify-end">
        <NewsActionButtons news={news} />
      </div>
      <SwitchToAppDialog />
    </>
  );
};

const ReadMoreSection: React.FunctionComponent<{
  utmLink: string;
  publisherName: string;
}> = ({ utmLink, publisherName }) => {
  const switchToApp = useOneLinkUrl(
    "https://link.luminews.my/ewC9/switchtoappbutton",
    true
  );

  return (
    <>
      <div className="mb-4 md:flex md:flex-row md:items-center">
        <div>
          <Image src={OverlayBImage} alt="overlay-b-image" />
        </div>
        <div className="-mt-10 flex flex-col items-center md:relative">
          <p className="text-center font-poppins text-text-darkest">
            <Trans>
              “I rarely give ratings to apps but this one deserves my 5-star
              rating.”
            </Trans>
          </p>
          <Link href={switchToApp}>
            <a className="mt-4 inline-flex">
              <div className="flex items-center rounded-2xl bg-primary px-4 py-4">
                <p className="text-center font-sen text-white">
                  <Trans>Continue on the app</Trans>
                </p>
              </div>
            </a>
          </Link>
        </div>
      </div>
      <div className="flex justify-center">
        <a
          href={utmLink}
          target="_blank"
          rel="noreferrer"
          className="cursor-pointer font-sen text-xs text-text-lightest hover:underline"
        >
          <Trans>Read the rest on</Trans> {publisherName}
        </a>
      </div>
    </>
  );
};

const NewsListSection = ({ news }) => {
  return (
    <>
      <div className="flex flex-col items-stretch divide-y divide-light-background">
        {news.map((news) => (
          <div className="py-4" key={news.id}>
            <NewNewsCard news={news} showPublisher />
          </div>
        ))}
      </div>
    </>
  );
};

export default NewsSummaryPage;
